import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import CatalogModal from './CatalogModal';

const services = [
  {
    title: 'Jardins Verticais',
    description: 'Jardins verticais naturais, artificiais, preservados e musgo moss.',
    image: '/images/service/Jardins verticais.jpg',
    link: '/jardins-verticais',
    features: ['Vertical Natural', 'Vertical Preservado', 'Vertical Artificial', 'Musgo Moss'],
  },
  {
    title: 'Paisagismo Permanente',
    description: 'Árvores, arranjos, quadros, nichos, cerejeiras.',
    image: '/images/service/Paisagismo Permanente.jpeg',
    link: '/paisagismo',
    features: ['Árvores Decorativas', 'Arranjos', 'Quadros Verdes', 'Nichos'],
  },
  {
    title: 'Paisagismo Natural',
    description: 'Projetos, venda de materiais e execução de serviços.',
    image: '/images/service/Paisagismo Natural.jpg',
    link: '/paisagismo',
    features: ['Projetos de Paisagismo', 'Jardinagem', 'Manutenção', 'Consultoria'],
  },
];

const ServicesSection = () => {
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section className="section-padding bg-white relative overflow-hidden">
      {/* Decorative Elements */}
      <motion.div
        className="absolute top-0 right-0 w-96 h-96 bg-conforto/40 rounded-full blur-3xl opacity-30"
        animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
        transition={{ repeat: Infinity, duration: 20 }}
      />

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-natureza font-semibold text-sm uppercase tracking-wider">
            Serviços
          </span>
          <h2 className="text-floresta font-display mt-4 mb-6">
            O que <span className="text-natureza italic">oferecemos</span>
          </h2>
          <p className="text-jardim text-lg max-w-3xl mx-auto">
            Soluções completas em jardins verticais, paisagismo e jardinagem para
            transformar seus ambientes
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -10 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-natureza/95 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="text-white text-center px-6">
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="flex items-center justify-center gap-2"
                        >
                          <span className="w-2 h-2 bg-argila rounded-full" />
                          {feature}
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-2xl font-display font-bold text-floresta mb-3 group-hover:text-natureza transition-colors">
                  {service.title}
                </h3>
                <p className="text-jardim mb-4 leading-relaxed">
                  {service.description}
                </p>

                <Link
                  to={service.link}
                  className="inline-flex items-center gap-2 text-primary-500 font-semibold hover:gap-4 transition-all duration-300"
                >
                  Saiba Mais
                  <FaArrowRight className="group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12"
        >
          <button
            onClick={() => setIsCatalogModalOpen(true)}
            className="btn-outline inline-block"
          >
            Ver Catálogo Completo
          </button>
        </motion.div>
      </div>

      {/* Catalog Modal */}
      <CatalogModal
        isOpen={isCatalogModalOpen}
        onClose={() => setIsCatalogModalOpen(false)}
      />
    </section>
  );
};

export default ServicesSection;
